import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BurgerIngredient, BurgerOrder } from '../types';
import { mockDataService } from '../services/mockData';
import { Plus, Minus, ShoppingCart, Truck, Star, ChefHat } from 'lucide-react';
import './BurgerBuilder.css';

const BurgerBuilder: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [selectedIngredients, setSelectedIngredients] = useState<BurgerIngredient[]>([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isOrdering, setIsOrdering] = useState(false);

  // Бургерийн орцууд
  const ingredients: BurgerIngredient[] = [
    // Талх
    { id: 'bread1', name: 'Classic Bun', nameMn: '🍞 Ердийн талх', price: 500, category: 'bread' },
    { id: 'bread2', name: 'Sesame Bun', nameMn: '🥖 Кунжуттай талх', price: 700, category: 'bread' },
    { id: 'bread3', name: 'Whole Wheat Bun', nameMn: '🌾 Бүхэл үрийн талх', price: 800, category: 'bread' },
    
    // Мах
    { id: 'meat1', name: 'Beef Patty', nameMn: '🥩 Үхрийн мах', price: 2500, category: 'meat' },
    { id: 'meat2', name: 'Chicken Patty', nameMn: '🐔 Тахианы мах', price: 2000, category: 'meat' },
    { id: 'meat3', name: 'Double Beef', nameMn: '🥩🥩 Хос үхрийн мах', price: 4000, category: 'meat' },
    
    // Ногоо
    { id: 'veg1', name: 'Lettuce', nameMn: '🥬 Салат ногоо', price: 300, category: 'vegetable' },
    { id: 'veg2', name: 'Tomato', nameMn: '🍅 Улаан лооль', price: 400, category: 'vegetable' },
    { id: 'veg3', name: 'Onion', nameMn: '🧅 Сонгино', price: 200, category: 'vegetable' },
    { id: 'veg4', name: 'Pickles', nameMn: '🥒 Даршилсан ногоо', price: 300, category: 'vegetable' },
    
    // Бяслаг
    { id: 'cheese1', name: 'Cheddar', nameMn: '🧀 Чеддар бяслаг', price: 600, category: 'cheese' },
    { id: 'cheese2', name: 'Swiss', nameMn: '🧀 Швейцарь бяслаг', price: 700, category: 'cheese' },
    
    // Сүүс
    { id: 'sauce1', name: 'Ketchup', nameMn: '🍅 Кетчуп', price: 100, category: 'sauce' },
    { id: 'sauce2', name: 'Mayo', nameMn: '🥄 Майонез', price: 100, category: 'sauce' },
    { id: 'sauce3', name: 'BBQ Sauce', nameMn: '🍖 BBQ сүүс', price: 150, category: 'sauce' },
  ];

  const addIngredient = (ingredient: BurgerIngredient) => {
    setSelectedIngredients([...selectedIngredients, ingredient]);
  };

  const removeIngredient = (index: number) => {
    const newIngredients = selectedIngredients.filter((_, i) => i !== index);
    setSelectedIngredients(newIngredients);
  };

  const getIngredientCount = (ingredientId: string) => {
    return selectedIngredients.filter(ing => ing.id === ingredientId).length;
  };

  const removeLastOfType = (ingredientId: string) => {
    const lastIndex = selectedIngredients.map(ing => ing.id).lastIndexOf(ingredientId);
    if (lastIndex !== -1) {
      removeIngredient(lastIndex);
    }
  };

  const getTotalPrice = () => {
    return selectedIngredients.reduce((total, ingredient) => total + ingredient.price, 0);
  };

  const groupIngredientsByCategory = () => {
    return ingredients.reduce((groups, ingredient) => {
      const category = ingredient.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(ingredient);
      return groups;
    }, {} as Record<string, BurgerIngredient[]>);
  };

  const categoryNames = {
    bread: 'Талх',
    meat: 'Мах',
    cheese: 'Бяслаг',
    vegetable: 'Ногоо',
    sauce: 'Сүүс'
  };

  const handleOrder = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (selectedIngredients.length === 0) {
      alert('Орц сонгоно уу!');
      return;
    }

    if (!customerInfo.name || !customerInfo.phone || !customerInfo.address) {
      alert('Бүх мэдээллийг бөглөнө үү!');
      return;
    }

    setIsOrdering(true);

    try {
      const order: Omit<BurgerOrder, 'id'> = {
        ingredients: selectedIngredients,
        totalPrice: getTotalPrice(),
        customerEmail: currentUser.email || '',
        customerName: customerInfo.name,
        customerPhone: customerInfo.phone,
        deliveryAddress: customerInfo.address,
        status: 'pending',
        orderDate: new Date()
      };

      console.log('Захиалга хадгалж байна:', order);

      // Demo config шалгах
      if (auth.app.options.apiKey === 'demo-api-key') {
        console.log('🎮 Demo режимд ажиллаж байна, mock service ашиглана');
        const savedOrder = await mockDataService.saveOrder(order);
        console.log('✅ Demo service дээр захиалга хадгалагдлаа:', savedOrder.id);
        alert(`🎮 Захиалга амжилттай илгээгдлээ! (Demo режим)\n📦 Захиалгын дугаар: ${savedOrder.id?.slice(-6)}`);
      } else {
        try {
          // Жинхэнэ Firebase ашиглах оролдлого
          const docRef = await addDoc(collection(db, 'orders'), {
            ...order,
            orderDate: new Date(),
            createdAt: new Date(),
          });
          console.log('✅ Firebase дээр захиалга хадгалагдлаа, ID:', docRef.id);
          alert(`🎉 Захиалга амжилттай илгээгдлээ!\n📦 Захиалгын дугаар: ${docRef.id.slice(-6)}\n🔥 Firebase дээр хадгалагдлаа.`);
        } catch (firebaseError: any) {
          console.error('❌ Firebase алдаа:', firebaseError);
          console.log('🔄 Demo режимд шилжиж байна...');
          
          // Firebase алдаа гарвал mock service ашиглах
          const savedOrder = await mockDataService.saveOrder(order);
          console.log('✅ Demo service дээр захиалга хадгалагдлаа:', savedOrder.id);
          alert(`🎮 Захиалга амжилттай илгээгдлээ! (Demo режим)\n📦 Захиалгын дугаар: ${savedOrder.id?.slice(-6)}`);
        }
      }
      
      setSelectedIngredients([]);
      setCustomerInfo({ name: '', phone: '', address: '' });
      navigate('/orders');
    } catch (error) {
      console.error('Захиалга илгээхэд алдаа гарлаа:', error);
      alert('Захиалга илгээхэд алдаа гарлаа. Дахин оролдоно уу.');
    } finally {
      setIsOrdering(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="burger-builder">
        <div className="auth-required">
          <h2>Нэвтрэх шаардлагатай</h2>
          <p>Бургер захиалахын тулд эхлээд нэвтэрнэ үү.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">
            Нэвтрэх
          </button>
        </div>
      </div>
    );
  }

  const groupedIngredients = groupIngredientsByCategory();

  return (
    <div className="burger-builder">
      <div className="builder-header">
        <div className="header-content">
          <h1 className="page-title">
            <ChefHat size={32} />
            <span>Бургер бүтээх</span>
          </h1>
          <p className="page-subtitle">Таны төгс бургерыг бүтээцгээе!</p>
        </div>
        <div className="header-visual">
          <div className="burger-icon">🍔</div>
        </div>
      </div>
      
      <div className="builder-container">
        <div className="ingredients-section">
          <div className="section-header">
            <h2 className="section-title">Орцуудыг сонгоно уу</h2>
            <p className="section-subtitle">Дуртай орцуудаа нэмээд өөрийн бургерыг бүтээнэ үү</p>
          </div>
          
          {Object.entries(groupedIngredients).map(([category, categoryIngredients]) => (
            <div key={category} className="ingredient-category">
              <h3 className="category-title">{categoryNames[category as keyof typeof categoryNames]}</h3>
              <div className="ingredients-grid">
                {categoryIngredients.map((ingredient) => {
                  const count = getIngredientCount(ingredient.id);
                  return (
                    <div key={ingredient.id} className={`ingredient-card ${count > 0 ? 'selected' : ''}`}>
                      <div className="ingredient-info">
                        <h4 className="ingredient-name">{ingredient.nameMn}</h4>
                        <p className="ingredient-price">{ingredient.price.toLocaleString()}₮</p>
                      </div>
                      
                      <div className="ingredient-actions">
                        {count > 0 && (
                          <button
                            onClick={() => removeLastOfType(ingredient.id)}
                            className="btn btn-icon btn-minus"
                          >
                            <Minus size={16} />
                          </button>
                        )}
                        
                        {count > 0 && (
                          <span className="ingredient-count">{count}</span>
                        )}
                        
                        <button
                          onClick={() => addIngredient(ingredient)}
                          className="btn btn-icon btn-plus"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="order-section">
          <div className="burger-preview">
            <div className="preview-header">
              <h3 className="preview-title">
                <ShoppingCart size={20} />
                <span>Таны бургер</span>
              </h3>
              <div className="burger-visual">
                <div className="burger-stack">
                  {selectedIngredients.length === 0 ? (
                    <div className="empty-burger">
                      <div className="empty-icon">🍽️</div>
                      <p>Орц сонгоно уу</p>
                    </div>
                  ) : (
                    <div className="burger-layers">
                      <div className="burger-layer top-bun">🍞</div>
                      {selectedIngredients.map((ingredient, index) => (
                        <div key={index} className="burger-layer ingredient-layer">
                          {ingredient.nameMn.split(' ')[0]}
                        </div>
                      ))}
                      <div className="burger-layer bottom-bun">🍞</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {selectedIngredients.length > 0 && (
              <div className="ingredients-summary">
                <h4>Орцууд:</h4>
                <div className="selected-ingredients">
                  {selectedIngredients.map((ingredient, index) => (
                    <div key={index} className="selected-ingredient">
                      <span className="ingredient-text">{ingredient.nameMn}</span>
                      <div className="ingredient-actions">
                        <span className="price">{ingredient.price.toLocaleString()}₮</span>
                        <button
                          onClick={() => removeIngredient(index)}
                          className="btn btn-icon btn-remove"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="total-section">
              <div className="total-price">
                <span>Нийт дүн:</span>
                <span className="price-amount">{getTotalPrice().toLocaleString()}₮</span>
              </div>
              <div className="delivery-info">
                <Truck size={16} />
                <span>15 минутын хүргэлт</span>
              </div>
            </div>
          </div>

          <div className="customer-info">
            <h3 className="form-title">
              <Truck size={20} />
              <span>Хүргэлтийн мэдээлэл</span>
            </h3>
            
            <div className="form-group">
              <label className="form-label">Нэр *</label>
              <input
                type="text"
                className="form-control"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                placeholder="Таны нэрээ оруулна уу"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Утасны дугаар *</label>
              <input
                type="tel"
                className="form-control"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                placeholder="99112233"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Хүргэх хаяг *</label>
              <textarea
                className="form-control"
                value={customerInfo.address}
                onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                placeholder="Хүргэх хаягаа дэлгэрэнгүй оруулна уу"
                rows={3}
              />
            </div>
            
            <div className="order-summary">
              <div className="summary-item">
                <Star size={16} />
                <span>Чанартай орц</span>
              </div>
              <div className="summary-item">
                <Truck size={16} />
                <span>Хурдан хүргэлт</span>
              </div>
              <div className="summary-item">
                <ChefHat size={16} />
                <span>Мэргэжлийн повар</span>
              </div>
            </div>
            
            <button
              onClick={handleOrder}
              disabled={isOrdering || selectedIngredients.length === 0}
              className={`btn btn-order ${selectedIngredients.length === 0 ? 'disabled' : ''}`}
            >
              {isOrdering ? (
                <>
                  <div className="spinner"></div>
                  <span>Захиалж байна...</span>
                </>
              ) : (
                <>
                  <ShoppingCart size={20} />
                  <span>Захиалах - {getTotalPrice().toLocaleString()}₮</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BurgerBuilder;
